import moment from 'moment';

/**
 * Obtiene desde el JSON que publica el servidor las fechas de las imágenes que
 * realmente están disponibles, acotadas a la ventana temporal pedida.
 *
 * Reemplaza a generaListadoImagenes: antes se suponían los nombres de archivo y se
 * sondeaba cuál respondía 200; ahora el servidor informa el listado y solo hay que
 * recortarlo.
 *
 * La ventana se cuenta desde la imagen más nueva del listado, no desde la hora del
 * cliente: así el bucle siempre termina en la última imagen publicada, aunque la
 * publicación venga atrasada o el reloj del cliente esté corrido.
 *
 * @param {Object} config
 * @param {string} config.LISTADO_URL - JSON con las rutas publicadas.
 * @param {string} config.CLAVE_LISTADO - clave del JSON que contiene el array de rutas.
 * @param {string[]} config.prefijos - prefijos a considerar; solo devuelve las fechas presentes en todos.
 * @param {string} config.extencion - extensión de los archivos.
 * @param {string} config.IMAGE_BASE_NAME_FORMAT - formato moment de la fecha dentro del nombre.
 * @param {Array} config.MAX_DATE_INTERVAL - ventana a conservar, en argumentos de moment.subtract.
 * @returns {Promise<string[]>} fechas de la más nueva a la más vieja.
 */
export const obtieneListadoImagenes = async ({ LISTADO_URL, CLAVE_LISTADO, prefijos, extencion, IMAGE_BASE_NAME_FORMAT, MAX_DATE_INTERVAL }) => {
    const respuesta = await fetch(LISTADO_URL, { cache: 'no-store' });
    if (!respuesta.ok) throw new Error(`No se pudo obtener el listado de imágenes (HTTP ${respuesta.status})`);

    const rutas = (await respuesta.json())[CLAVE_LISTADO] ?? [];

    const fechasPorPrefijo = prefijos.map((prefijo) => extraeFechas({ rutas, prefijo, extencion, IMAGE_BASE_NAME_FORMAT }));

    // Solo sirven las fechas presentes en todos los prefijos, porque los paneles se muestran juntos.
    const fechas = [...fechasPorPrefijo[0]].filter((fecha) => fechasPorPrefijo.every((disponibles) => disponibles.has(fecha)));

    // De la más nueva a la más vieja: el resto de la aplicación asume ese orden.
    fechas.sort().reverse();
    if (fechas.length === 0) return [];

    const limite = moment.utc(fechas[0], IMAGE_BASE_NAME_FORMAT).subtract(...MAX_DATE_INTERVAL);
    return fechas.filter((fecha) => moment.utc(fecha, IMAGE_BASE_NAME_FORMAT).isSameOrAfter(limite));
};

/**
 * Junta las fechas de las rutas que corresponden a un prefijo, descartando lo que no
 * parsea con el formato esperado.
 */
const extraeFechas = ({ rutas, prefijo, extencion, IMAGE_BASE_NAME_FORMAT }) => {
    const terminacion = `.${extencion}`;

    return rutas.reduce((fechas, ruta) => {
        const nombre = ruta.split('/').pop();
        if (!nombre.startsWith(prefijo) || !nombre.endsWith(terminacion)) return fechas;

        const fecha = nombre.slice(prefijo.length, -terminacion.length);
        if (moment.utc(fecha, IMAGE_BASE_NAME_FORMAT, true).isValid()) fechas.add(fecha);

        return fechas;
    }, new Set());
};
